import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllOrganizations } from '../../slice/organizationsSlice';
import { fetchAllSubscriptions, createSubscription, updateSubscription, deleteSubscription, fetchTasksByOrganization, clearOrganizationTasks } from '../../slice/subscriptionSlice';
import { fetchDepartments, createDepartment, updateDepartment, deleteDepartment, updateDepartmentsOrder } from '../../slice/departmentsSlice';
import { fetchAllUsers } from '../../slice/usersSlice';
import { useThemeColor } from '../../hooks/useThemeColor';
import { useToast } from '../../components/Toast';
import SharePointConnectionModal from './components/SharePointConnectionModal';
import KnowledgeAreaManagerModal from './components/KnowledgeAreaManagerModal';
import { fetchSharePointConnection, fetchConfiguredSites, addConfiguredSite, removeConfiguredSite, updateConfiguredSite, checkSharePointSiteAccess, clearSiteCheckResult, checkExistingSiteAccess, fetchKnowledgeAreas } from '../../slice/sharepointSlice';
import DeleteModal from '../../components/atomic/DeleteModal';
import { useTranslation } from '../../i18n/LanguageContext';
import { formatSharePointError } from '../../utils/sharepointErrorMessage';
import { Search, Plus, MoreHorizontal, Settings, Building2, X, Pencil, Trash2, Eye, ChevronDown, Check, ArrowLeft, Globe, Wifi, WifiOff, Link2, GripVertical, Copy, RefreshCw, AlertTriangle } from 'lucide-react';
import AgentOrder from './AgentOrder';
import TerminologyTab from './components/TerminologyTab';
import { isAdminRole } from '../../components/constants';

// ============== Organization Dropdown ==============
const OrganizationDropdown = ({ organizations, selectedId, currentOrgId, aispaceColor, disabled, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = React.useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const selectedOrg = organizations.find(o => o.organization_id === selectedId);
  const triggerLabel = selectedOrg ? selectedOrg.organization_name : t('manageOrganization.subModalOrgPlaceholder');

  return (
    <div>
      <label className="block text-[14px] font-medium text-[#111827] mb-2">{t('manageOrganization.subModalOrgLabel')}</label>
      <div className="relative" ref={wrapperRef}>
        <button
          type="button"
          onClick={() => !disabled && setOpen(v => !v)}
          disabled={disabled}
          className="w-full flex items-center justify-between border border-[#E5E7EB] rounded-lg px-4 py-3 text-[14px] bg-white hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none"
          style={open ? { borderColor: aispaceColor } : {}}
        >
          <span className={selectedOrg ? 'text-[#111827]' : 'text-[#9CA3AF]'}>{triggerLabel}</span>
          <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-30 max-h-[260px] overflow-y-auto py-1">
            {organizations.length === 0 ? (
              <div className="px-4 py-3 text-[13px] text-[#9CA3AF]">{t('manageOrganization.subModalNoOrgs')}</div>
            ) : (
              organizations.map((org) => {
                const isSelected = org.organization_id === selectedId;
                const isAllowed = org.organization_id === currentOrgId;
                return (
                  <button
                    key={org.organization_id}
                    type="button"
                    onClick={() => {
                      if (!isAllowed) return;
                      onChange(org.organization_id);
                      setOpen(false);
                    }}
                    disabled={!isAllowed}
                    className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] transition-colors ${
                      isAllowed ? 'text-[#111827] hover:bg-[#F9FAFB] cursor-pointer' : 'text-[#9CA3AF] cursor-not-allowed'
                    } ${isSelected ? 'bg-[#F3F4F6]' : ''}`}
                  >
                    <span className="truncate">{org.organization_name}</span>
                    {isSelected && <Check size={16} style={{ color: aispaceColor }} />}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ============== Department Modal ==============
const DepartmentModal = ({ isOpen, onClose, onConfirm, initial = {}, aispaceColor, saving = false }) => {
  const [form, setForm] = useState({ department_name: '', department_description: '' });
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      if (initial && initial.id) {
        setForm({
          department_name: initial.department_name || '',
          department_description: initial.department_description || '',
        });
      } else {
        setForm({ department_name: '', department_description: '' });
      }
    }
  }, [initial, isOpen]);

  if (!isOpen) return null;

  const isEdit = !!initial?.id;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]">
      <div className="bg-white rounded-xl w-[480px] shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
          <h2 className="text-[16px] font-semibold text-[#111827]">
            {isEdit ? t('manageOrganization.deptModalEditTitle') : t('manageOrganization.deptModalCreateTitle')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors"
            disabled={saving}
          >
            <X size={20} className="text-[#6B7280]" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-[14px] font-medium text-[#111827] mb-2">
              {t('manageOrganization.deptModalNameLabel')}
            </label>
            <input
              type="text"
              className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-[14px] text-[#374151] focus:outline-none focus:ring-0 placeholder-[#9CA3AF]"
              style={{}}
              onFocus={(e) => e.target.style.borderColor = aispaceColor}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
              value={form.department_name}
              onChange={(e) => setForm({ ...form, department_name: e.target.value })}
              placeholder={t('manageOrganization.deptModalNamePlaceholder')}
              disabled={saving}
            />
          </div>
          <div>
            <label className="block text-[14px] font-medium text-[#111827] mb-2">
              {t('manageOrganization.deptModalDescriptionLabel')}
            </label>
            <textarea
              className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-[14px] text-[#374151] focus:outline-none focus:ring-0 placeholder-[#9CA3AF] resize-none"
              style={{}}
              onFocus={(e) => e.target.style.borderColor = aispaceColor}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
              value={form.department_description}
              onChange={(e) => setForm({ ...form, department_description: e.target.value })}
              placeholder={t('manageOrganization.deptModalDescriptionPlaceholder')}
              rows="5"
              disabled={saving}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB]">
          <button
            className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50"
            onClick={onClose}
            disabled={saving}
          >
            {t('common.cancel')}
          </button>
          <button
            className="flex items-center justify-center gap-2 px-5 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[160px]"
            style={{ backgroundColor: aispaceColor }}
            onClick={() => {
              if (!form.department_name.trim()) return;
              onConfirm(form);
            }}
            disabled={!form.department_name.trim() || saving}
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {isEdit ? t('manageOrganization.deptModalSaving') : t('manageOrganization.deptModalCreating')}
              </>
            ) : (
              isEdit ? t('manageOrganization.deptModalSubmitSave') : t('manageOrganization.deptModalSubmitCreate')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============== Subscription Modal ==============
const SubscriptionModal = ({ isOpen, onClose, onConfirm, initial = null, organizationId, aispaceColor, saving = false }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { organizations } = useSelector(state => state.organizations);
  const { organizationTasks, organizationTasksLoading } = useSelector(state => state.subscriptions);

  const [form, setForm] = useState({
    subscription_name: '',
    subscription_description: '',
    organization_id: '',
    task_limits: [],
  });

  const isEdit = !!initial?.subscription_id;

  useEffect(() => {
    if (isOpen) {
      if (initial && initial.subscription_id) {
        setForm({
          subscription_name: initial.subscription_name || '',
          subscription_description: initial.subscription_description || '',
          organization_id: initial.organization_id || organizationId || '',
          task_limits: initial.task_limits || [],
        });
        const oid = initial.organization_id || organizationId;
        if (oid) dispatch(fetchTasksByOrganization(oid));
      } else {
        setForm({
          subscription_name: '',
          subscription_description: '',
          organization_id: organizationId || '',
          task_limits: [],
        });
        if (organizationId) dispatch(fetchTasksByOrganization(organizationId));
      }
    } else {
      dispatch(clearOrganizationTasks());
    }
  }, [isOpen, organizationId, initial, dispatch]);

  // Blank input maps to `null` (unlimited), never `0` — 0 would mean "no
  // pages allowed at all", which isn't what an empty field means here.
  const handleTaskLimitChange = (taskId, field, rawValue) => {
    const value = rawValue === '' ? null : (parseInt(rawValue, 10) || 0);
    setForm(prev => {
      const existing = prev.task_limits.find(t => t.task_id === taskId);
      let updated;
      if (existing) {
        updated = prev.task_limits.map(t =>
          t.task_id === taskId ? { ...t, [field]: value } : t
        );
      } else {
        updated = [...prev.task_limits, { task_id: taskId, [field]: value }];
      }
      return { ...prev, task_limits: updated };
    });
  };

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!form.subscription_name.trim() || !form.organization_id) return;
    const filteredTaskLimits = form.task_limits.map(t => ({
      task_id: t.task_id,
      standard_pages_per_month: t.standard_pages_per_month ?? null,
      upload_pages_per_session: t.upload_pages_per_session ?? null,
    }));
    const payload = {
      ...form,
      task_limits: filteredTaskLimits,
    };
    if (isEdit) payload.subscription_id = initial.subscription_id;
    onConfirm(payload);
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]">
      <div className="bg-white rounded-xl w-[680px] max-h-[90vh] shadow-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB] flex-shrink-0">
          <h2 className="text-[16px] font-semibold text-[#111827]">
            {isEdit ? t('manageOrganization.subModalEditTitle') : t('manageOrganization.subModalCreateTitle')}
          </h2>
          <button onClick={onClose} disabled={saving} className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors">
            <X size={20} className="text-[#6B7280]" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Section 1: Subscription Information */}
          <div className="border border-[#E5E7EB] rounded-xl overflow-visible">
            <div className="bg-[#F9FAFB] px-5 py-3 border-b border-[#E5E7EB] rounded-t-xl">
              <h3 className="text-[14px] font-semibold text-[#111827]">{t('manageOrganization.subModalInfoSection')}</h3>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-[14px] font-medium text-[#111827] mb-2">{t('manageOrganization.subModalNameLabel')}</label>
                <input
                  type="text"
                  className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-[14px] text-[#374151] focus:outline-none focus:ring-0 placeholder-[#9CA3AF]"
                  onFocus={(e) => e.target.style.borderColor = aispaceColor}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                  value={form.subscription_name}
                  onChange={(e) => setForm({ ...form, subscription_name: e.target.value })}
                  placeholder={t('manageOrganization.subModalNamePlaceholder')}
                  disabled={saving}
                />
              </div>
              <div>
                <label className="block text-[14px] font-medium text-[#111827] mb-2">{t('manageOrganization.subModalDescriptionLabel')}</label>
                <textarea
                  className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-[14px] text-[#374151] focus:outline-none focus:ring-0 placeholder-[#9CA3AF] resize-none"
                  onFocus={(e) => e.target.style.borderColor = aispaceColor}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                  value={form.subscription_description}
                  onChange={(e) => setForm({ ...form, subscription_description: e.target.value })}
                  placeholder={t('manageOrganization.subModalDescriptionPlaceholder')}
                  rows="4"
                  disabled={saving}
                />
              </div>
              <OrganizationDropdown
                organizations={organizations}
                selectedId={form.organization_id}
                currentOrgId={organizationId}
                aispaceColor={aispaceColor}
                disabled={saving}
                onChange={(orgId) => {
                  setForm(prev => ({ ...prev, organization_id: orgId, task_limits: [] }));
                  if (orgId) dispatch(fetchTasksByOrganization(orgId));
                  else dispatch(clearOrganizationTasks());
                }}
              />
            </div>
          </div>

          {/* Section 2: Two settings per agent — always show when org is selected */}
          {form.organization_id && (
            <div className="border border-[#E5E7EB] rounded-xl">
              <div className="bg-[#F9FAFB] px-5 py-3 border-b border-[#E5E7EB] rounded-t-xl">
                <h3 className="text-[14px] font-semibold text-[#111827]">{t('manageOrganization.subModalAgentLimitsSection')}</h3>
                <p className="text-[12px] text-[#6B7280] mt-0.5">{t('manageOrganization.subModalAgentLimitsHint')}</p>
              </div>
              <div className="px-5 py-4">
                {organizationTasksLoading ? (
                  <div className="flex justify-center items-center py-6">
                    <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: aispaceColor, borderTopColor: 'transparent' }}></div>
                  </div>
                ) : organizationTasks.length > 0 ? (
                  <div>
                    <div className="flex items-center py-2 border-b border-[#E5E7EB]">
                      <div className="flex-1 text-[13px] font-medium text-[#6B7280]">{t('manageOrganization.subModalAgentColumn')}</div>
                      <div className="w-[170px] text-[13px] font-medium text-[#6B7280]">{t('manageOrganization.subModalStandardPagesColumn')}</div>
                      <div className="w-[170px] text-[13px] font-medium text-[#6B7280]">{t('manageOrganization.subModalUploadPagesColumn')}</div>
                    </div>
                    {organizationTasks.map(task => {
                      const taskLimit = form.task_limits.find(t => t.task_id === task.id);
                      return (
                        <div key={task.id} className="flex items-center py-4 border-b border-[#F3F4F6] last:border-b-0">
                          <div className="flex-1 text-[14px] text-[#374151]">{task.name}</div>
                          <div className="w-[170px] pr-3">
                            <input
                              type="number"
                              min="0"
                              className="w-[120px] border border-[#E5E7EB] rounded-lg px-3 py-2.5 text-[14px] text-[#374151] text-center focus:outline-none focus:ring-0"
                              onFocus={(e) => e.target.style.borderColor = aispaceColor}
                              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                              placeholder={t('manageOrganization.subModalUnlimitedPlaceholder')}
                              value={taskLimit?.standard_pages_per_month ?? ''}
                              onChange={(e) => handleTaskLimitChange(task.id, 'standard_pages_per_month', e.target.value)}
                              disabled={saving}
                            />
                          </div>
                          <div className="w-[170px]">
                            <input
                              type="number"
                              min="0"
                              className="w-[120px] border border-[#E5E7EB] rounded-lg px-3 py-2.5 text-[14px] text-[#374151] text-center focus:outline-none focus:ring-0"
                              onFocus={(e) => e.target.style.borderColor = aispaceColor}
                              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                              placeholder={t('manageOrganization.subModalUnlimitedPlaceholder')}
                              value={taskLimit?.upload_pages_per_session ?? ''}
                              onChange={(e) => handleTaskLimitChange(task.id, 'upload_pages_per_session', e.target.value)}
                              disabled={saving}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-[13px] text-[#6B7280] italic py-4">{t('manageOrganization.subModalNoAgents')}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] flex-shrink-0">
          <button
            className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50"
            onClick={onClose}
            disabled={saving}
          >
            {t('common.cancel')}
          </button>
          <button
            className="flex items-center justify-center gap-2 px-5 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px]"
            style={{ backgroundColor: aispaceColor }}
            onClick={handleSubmit}
            disabled={!form.subscription_name.trim() || !form.organization_id || saving}
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t('manageOrganization.subModalSaving')}
              </>
            ) : (
              t('manageOrganization.subModalSubmit')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============== Main Component ==============
const ManageOrganization = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { aispaceColor } = useThemeColor();
  const { t } = useTranslation();
  const { organizations, error: organizationsError } = useSelector(state => state.organizations);
  const { subscriptions } = useSelector(state => state.subscriptions);
  const { departments, loading: deptLoading } = useSelector(state => state.departments);
  const { addToast, ToastContainer } = useToast();

  const [selectedTab, setSelectedTab] = useState('departments');
  const [agentOrderDirty, setAgentOrderDirty] = useState(false);
  const confirmLeaveAgentOrder = () => !agentOrderDirty || window.confirm(t('agentOrder.discardConfirm'));
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [spModalOpen, setSpModalOpen] = useState(false);
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [editSub, setEditSub] = useState(null);
  const [deleteSub, setDeleteSub] = useState(null);
  const [savingSub, setSavingSub] = useState(false);
  const [deptModalOpen, setDeptModalOpen] = useState(false);
  const [editDept, setEditDept] = useState(null);
  const [deleteDept, setDeleteDept] = useState(null);
  const [saving, setSaving] = useState(false);
  const itemsPerPage = 7;

  const { data: userData } = useSelector(state => state.admin);
  const isSuperAdmin = userData?.user_role === 'SUPER_ADMIN';
  const canOrderAgents = isAdminRole(userData?.user_role);
  const adminOrgId  = userData?.organization_id;

  const org = organizations.find(o => o.organization_id === orgId);
  const orgSubscription = subscriptions.find(s => s.organization_id === orgId);

  const allUsers = useSelector(state => state.all?.users || []);
  const orgUsers = allUsers.filter(u => u.organization_id === orgId);

  useEffect(() => {
    dispatch(fetchAllOrganizations());
    dispatch(fetchAllSubscriptions());
    dispatch(fetchAllUsers());
  }, [dispatch]);

  useEffect(() => {
    if (orgId) {
      dispatch(fetchDepartments(orgId));
    }
  }, [dispatch, orgId]);

  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleCreateDept = () => {
    setEditDept(null);
    setDeptModalOpen(true);
  };

  const handleEditDept = (dept) => {
    setEditDept(dept);
    setDeptModalOpen(true);
    setOpenDropdown(null);
  };

  const handleDeleteDept = (dept) => {
    setDeleteDept(dept);
    setOpenDropdown(null);
  };

  const handleDeptModalConfirm = async (form) => {
    if (saving) return;
    setSaving(true);
    try {
      if (editDept) {
        await dispatch(updateDepartment({ id: editDept.id, ...form })).unwrap();
      } else {
        await dispatch(createDepartment({ organization_id: orgId, ...form })).unwrap();
      }
      setDeptModalOpen(false);
      await dispatch(fetchDepartments(orgId));
      addToast(editDept ? t('manageOrganization.deptToastUpdated') : t('manageOrganization.deptToastCreated'), 'success');
    } catch (err) {
      addToast(t('manageOrganization.deptToastSaveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteDepartment(deleteDept.id)).unwrap();
      setDeleteDept(null);
      await dispatch(fetchDepartments(orgId));
      addToast(t('manageOrganization.deptToastDeleted'), 'success');
    } catch (err) {
      addToast(t('manageOrganization.deptToastDeleteError'), 'error');
    }
  };

  const handleSubModalConfirm = async (form) => {
    if (savingSub) return;
    setSavingSub(true);
    try {
      if (form.subscription_id) {
        await dispatch(updateSubscription(form)).unwrap();
      } else {
        await dispatch(createSubscription(form)).unwrap();
      }
      setSubModalOpen(false);
      setEditSub(null);
      await dispatch(fetchAllSubscriptions());
      addToast(form.subscription_id ? t('manageOrganization.subToastUpdated') : t('manageOrganization.subToastCreated'), 'success');
    } catch (err) {
      addToast(t('manageOrganization.subToastSaveError'), 'error');
    } finally {
      setSavingSub(false);
    }
  };

  const handleDeleteSubConfirm = async () => {
    try {
      await dispatch(deleteSubscription(deleteSub.subscription_id)).unwrap();
      setDeleteSub(null);
      await dispatch(fetchAllSubscriptions());
      addToast(t('manageOrganization.subToastDeleted'), 'success');
    } catch (err) {
      addToast(t('manageOrganization.subToastDeleteError'), 'error');
    }
  };

  if (!org) {
    if (organizationsError) {
      return (
        <div className="h-screen bg-white flex items-center justify-center">
          <div className="text-center px-6">
            <p className="text-[14px] text-red-600">{organizationsError}</p>
          </div>
        </div>
      );
    }
    return (
      <div className="h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: aispaceColor }}></div>
          <p className="text-[14px] text-[#6B7280]">{t('manageOrganization.loading')}</p>
        </div>
      </div>
    );
  }

  const filteredDepartments = departments.filter(dept =>
    dept.department_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dept.department_description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredDepartments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDepartments = filteredDepartments.slice(startIndex, startIndex + itemsPerPage);

  // ITEM 10: subscriptions tab hidden for ADMIN — only SUPER_ADMIN can manage subscriptions
  const tabs = [
    { id: 'departments',    label: t('manageOrganization.tabDepartments') },
    ...(canOrderAgents ? [{ id: 'agent-order', label: t('manageOrganization.tabAgentOrder') }] : []),
    ...(isSuperAdmin ? [{ id: 'subscriptions', label: t('manageOrganization.tabSubscriptions') }] : []),
    ...(isSuperAdmin ? [{ id: 'terminology', label: t('manageOrganization.tabTerminology') }] : []),
    { id: 'link-sharepoint', label: t('manageOrganization.tabSharepoint') },
  ];

  const departmentCount = departments.length;
  const userCount = orgUsers.length;

  return (
    <div className="h-screen bg-white overflow-hidden flex flex-col">
      <ToastContainer />

      <div className="flex-1 overflow-y-auto px-10 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-[14px] text-[#6B7280] mb-6">
          <button
            onClick={() => { if (confirmLeaveAgentOrder()) navigate('/settings'); }}
            className="flex items-center gap-1 hover:text-[#374151] transition-colors"
          >
            <Settings size={16} />
            <span>{t('manageOrganization.breadcrumbSettings')}</span>
          </button>
          <span>/</span>
          <span className="text-[#111827] font-medium">{t('manageOrganization.breadcrumbManage')}</span>
        </div>

        {/* Organization Header Card */}
        <div className="border border-[#E5E7EB] rounded-xl px-8 py-5 mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-[20px] font-semibold text-[#111827]">
              {org.organization_name}
            </h1>
            <div className="flex items-center gap-4 text-[14px] text-[#6B7280]">
              <span>
                {t('manageOrganization.summaryDepartments')}: <span className="font-semibold text-[#111827]">{departmentCount}</span>
              </span>
              <span className="text-[#D1D5DB]">&bull;</span>
              <span>
                {t('manageOrganization.summaryUsers')}: <span className="font-semibold text-[#111827]">{userCount}</span>
              </span>
              <span className="text-[#D1D5DB]">&bull;</span>
              <span>
                {t('manageOrganization.summarySubscription')}:{' '}
                {orgSubscription ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-medium bg-[#F3E8FF] text-[#7C3AED]">
                    {t('manageOrganization.summaryActive')}
                  </span>
                ) : (
                  <span className="text-[#6B7280]">{t('manageOrganization.summaryInactive')}</span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-6 border-b border-[#E5E7EB] mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                if (tab.id !== selectedTab && selectedTab === 'agent-order' && !confirmLeaveAgentOrder()) return;
                setSelectedTab(tab.id);
              }}
              className={`pb-3 text-[14px] font-medium transition-colors relative ${
                selectedTab !== tab.id ? 'text-[#6B7280] hover:text-[#374151]' : ''
              }`}
              style={selectedTab === tab.id ? { color: aispaceColor } : {}}
            >
              {tab.label}
              {selectedTab === tab.id && (
                <div
                  className="absolute bottom-0 left-0 right-0 h-[2px] rounded-full"
                  style={{ backgroundColor: aispaceColor }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {selectedTab === 'departments' && (
          <DepartmentsTab
            allDepartments={filteredDepartments}
            searchQuery={searchQuery}
            setSearchQuery={(q) => { setSearchQuery(q); setCurrentPage(1); }}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            openDropdown={openDropdown}
            setOpenDropdown={setOpenDropdown}
            aispaceColor={aispaceColor}
            onAdd={handleCreateDept}
            onEdit={handleEditDept}
            onDelete={handleDeleteDept}
          />
        )}

        {selectedTab === 'subscriptions' && (
          <SubscriptionsTab
            subscriptions={subscriptions.filter(s => s.organization_id === orgId)}
            aispaceColor={aispaceColor}
            addToast={addToast}
            onAdd={() => { setEditSub(null); setSubModalOpen(true); }}
            onEdit={(sub) => { setEditSub(sub); setSubModalOpen(true); }}
            onDelete={(sub) => setDeleteSub(sub)}
          />
        )}

        {selectedTab === 'terminology' && isSuperAdmin && (
          <TerminologyTab
            key={orgId}
            organizationId={orgId}
            aispaceColor={aispaceColor}
            addToast={addToast}
          />
        )}

        {selectedTab === 'agent-order' && canOrderAgents && (
          <AgentOrder
            key={orgId}
            organizationId={orgId}
            departments={departments}
            departmentsLoading={deptLoading}
            addToast={addToast}
            onDirtyChange={setAgentOrderDirty}
          />
        )}

        {selectedTab === 'link-sharepoint' && (
          <SharePointTab
            organizationId={orgId}
            organizationName={org.organization_name}
            aispaceColor={aispaceColor}
            onConnect={() => setSpModalOpen(true)}
            addToast={addToast}
          />
        )}
      </div>

      {/* Department Modal */}
      <DepartmentModal
        isOpen={deptModalOpen}
        onClose={() => setDeptModalOpen(false)}
        onConfirm={handleDeptModalConfirm}
        initial={editDept || {}}
        aispaceColor={aispaceColor}
        saving={saving}
      />

      {/* Delete Modal */}
      <DeleteModal
        isOpen={!!deleteDept}
        onClose={() => setDeleteDept(null)}
        onDelete={handleDeleteConfirm}
        title={t('manageOrganization.deptDeleteTitle')}
        message={t('manageOrganization.deptDeleteMessage')}
      />

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={subModalOpen}
        onClose={() => { setSubModalOpen(false); setEditSub(null); }}
        onConfirm={handleSubModalConfirm}
        initial={editSub}
        organizationId={orgId}
        aispaceColor={aispaceColor}
        saving={savingSub}
      />

      {/* Delete Subscription Modal */}
      <DeleteModal
        isOpen={!!deleteSub}
        onClose={() => setDeleteSub(null)}
        onDelete={handleDeleteSubConfirm}
        title={t('manageOrganization.subDeleteTitle')}
        message={t('manageOrganization.subDeleteMessage', { name: deleteSub?.subscription_name })}
      />

      {/* SharePoint Modal */}
      <SharePointConnectionModal
        isOpen={spModalOpen}
        onClose={() => {
          setSpModalOpen(false);
          dispatch(fetchConfiguredSites(orgId));
          dispatch(fetchSharePointConnection(orgId));
        }}
        organizationId={orgId}
        organizationName={org.organization_name}
      />
    </div>
  );
};

// ============== Departments Tab ==============
const DepartmentsTab = ({
  allDepartments,
  searchQuery,
  setSearchQuery,
  currentPage,
  setCurrentPage,
  openDropdown,
  setOpenDropdown,
  aispaceColor,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [sortedDepts, setSortedDepts] = useState([]);
  const draggedIndexRef = useRef(null);
  const sortedDeptsRef = useRef([]);
  const ITEMS_PER_PAGE = 7;

  useEffect(() => {
    const sorted = [...allDepartments].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    setSortedDepts(sorted);
    sortedDeptsRef.current = sorted;
  }, [allDepartments]);

  const localTotalPages = Math.ceil(sortedDepts.length / ITEMS_PER_PAGE);
  const pageStart = (currentPage - 1) * ITEMS_PER_PAGE;
  const localPaginated = sortedDepts.slice(pageStart, pageStart + ITEMS_PER_PAGE);

  const handleDragStart = (e, pageIndex) => {
    draggedIndexRef.current = pageStart + pageIndex;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, pageIndex) => {
    e.preventDefault();
    const absTarget = pageStart + pageIndex;
    const absDragged = draggedIndexRef.current;
    if (absDragged === null || absDragged === absTarget) return;
    const newSorted = [...sortedDeptsRef.current];
    const [moved] = newSorted.splice(absDragged, 1);
    newSorted.splice(absTarget, 0, moved);
    draggedIndexRef.current = absTarget;
    sortedDeptsRef.current = newSorted;
    setSortedDepts(newSorted);
  };

  const handleDragEnd = () => {
    draggedIndexRef.current = null;
    const orderList = sortedDeptsRef.current.map((d, idx) => ({ id: d.id, sort_order: idx }));
    dispatch(updateDepartmentsOrder(orderList));
  };

  const isEmpty = allDepartments.length === 0 && !searchQuery;
  const noResults = allDepartments.length === 0 && searchQuery;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl">
      {/* Search and Action Buttons */}
      <div className="flex items-center justify-between gap-4 px-8 py-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input
              type="text"
              placeholder={t('manageOrganization.deptSearchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-0"
              style={{}}
              onFocus={(e) => e.target.style.borderColor = aispaceColor}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>
        </div>
        <button
          className="flex items-center gap-2 px-5 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors"
          style={{ backgroundColor: aispaceColor }}
          onClick={onAdd}
        >
          <Plus size={18} />
          {t('manageOrganization.deptAddButton')}
        </button>
      </div>

      {/* Empty State */}
      {isEmpty && (
        <div className="flex flex-col items-center justify-center py-20 px-8">
          <div className="w-16 h-16 bg-[#F3F4F6] rounded-full flex items-center justify-center mb-4">
            <Building2 size={28} className="text-[#9CA3AF]" />
          </div>
          <h3 className="text-[16px] font-medium text-[#111827] mb-1">{t('manageOrganization.deptEmptyTitle')}</h3>
          <p className="text-[14px] text-[#6B7280]">{t('manageOrganization.deptEmptyHint')}</p>
        </div>
      )}

      {/* No Search Results */}
      {noResults && (
        <div className="flex flex-col items-center justify-center py-20 px-8">
          <div className="w-16 h-16 bg-[#F3F4F6] rounded-full flex items-center justify-center mb-4">
            <Search size={28} className="text-[#9CA3AF]" />
          </div>
          <h3 className="text-[16px] font-medium text-[#111827] mb-1">{t('manageOrganization.noResultsTitle')}</h3>
          <p className="text-[14px] text-[#6B7280]">{t('manageOrganization.noResultsHint')}</p>
        </div>
      )}

      {/* Table */}
      {localPaginated.length > 0 && (
        <>
          {/* Table Header */}
          <div className="flex items-center px-8 py-3 border-t border-b border-[#E5E7EB] bg-[#F9FAFB]">
            <div className="w-[28px] flex-shrink-0 mr-2"></div>
            <div className="flex-[2] text-[13px] font-medium text-[#6B7280]">{t('manageOrganization.deptColumnName')}</div>
            <div className="flex-[3] text-[13px] font-medium text-[#6B7280]">{t('manageOrganization.deptColumnDescription')}</div>
            <div className="flex-[1.5] text-[13px] font-medium text-[#6B7280]">{t('manageOrganization.deptColumnCreated')}</div>
            <div className="w-[50px] flex-shrink-0"></div>
          </div>

          {/* Table Body */}
          <div>
            {localPaginated.map((dept, index) => {
              const isLastItem = localPaginated.length > 3 && index === localPaginated.length - 1;
              const isDropdownOpen = openDropdown === dept.id;

              return (
                <div
                  key={dept.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center px-8 py-5 hover:bg-[#FAFAFA] transition-colors cursor-grab active:cursor-grabbing ${
                    index !== localPaginated.length - 1 ? 'border-b border-[#E5E7EB]' : ''
                  }`}
                >
                  <div className="w-[28px] flex-shrink-0 mr-2 text-[#D1D5DB] hover:text-[#9CA3AF]">
                    <GripVertical size={18} />
                  </div>
                  <div className="flex-[2] text-[14px] font-medium text-[#111827] truncate pr-4">
                    {dept.department_name}
                  </div>
                  <div className="flex-[3] text-[14px] text-[#6B7280] truncate pr-4">
                    {dept.department_description || '-'}
                  </div>
                  <div className="flex-[1.5] text-[14px] text-[#6B7280]">
                    {dept.created_at
                      ? new Date(dept.created_at).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })
                      : '-'}
                  </div>
                  <div className="w-[50px] flex-shrink-0 flex items-center justify-end relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdown(isDropdownOpen ? null : dept.id);
                      }}
                      className="p-1.5 text-[#6B7280] hover:text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
                    >
                      <MoreHorizontal size={18} />
                    </button>

                    {isDropdownOpen && (
                      <div className={`absolute right-0 ${isLastItem ? 'bottom-full mb-2' : 'top-full mt-2'} w-[220px] bg-white border border-[#E5E7EB] rounded-xl shadow-lg z-[999] py-2`}>
                        <button
                          onClick={() => onEdit(dept)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors"
                        >
                          <Pencil size={16} className="text-[#374151]" />
                          {t('manageOrganization.deptActionEdit')}
                        </button>
                        <button
                          onClick={() => onDelete(dept)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors"
                        >
                          <Trash2 size={16} className="text-[#374151]" />
                          {t('manageOrganization.deptActionDelete')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-8 py-4 border-t border-[#E5E7EB]">
            <span className="text-[14px] text-[#6B7280]">
              {t('pagination.pageOf', { current: currentPage, total: localTotalPages || 1 })}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.back')}
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, localTotalPages))}
                disabled={currentPage === localTotalPages || localTotalPages === 0}
                className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ============== Subscriptions Tab ==============
const SubscriptionsTab = ({ subscriptions = [], aispaceColor, addToast, onAdd, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [viewUsageSub, setViewUsageSub] = useState(null);
  const itemsPerPage = 7;

  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const filteredSubscriptions = subscriptions.filter(sub =>
    sub.subscription_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sub.subscription_description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredSubscriptions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSubscriptions = filteredSubscriptions.slice(startIndex, startIndex + itemsPerPage);

  const isEmpty = filteredSubscriptions.length === 0 && !searchQuery;
  const noResults = filteredSubscriptions.length === 0 && searchQuery;

  // Keep viewUsageSub's data fresh when subscriptions refresh (e.g. after an edit)
  useEffect(() => {
    if (viewUsageSub) {
      const updated = subscriptions.find(s => s.subscription_id === viewUsageSub.subscription_id);
      if (updated) setViewUsageSub(updated);
    }
  }, [subscriptions]);

  // Usage View
  if (viewUsageSub) {
    return (
      <CreditUsageView
        subscription={viewUsageSub}
        aispaceColor={aispaceColor}
        onBack={() => setViewUsageSub(null)}
      />
    );
  }

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl">
      {/* Search and Add Button */}
      <div className="flex items-center justify-between gap-4 px-8 py-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input
              type="text"
              placeholder={t('manageOrganization.subSearchPlaceholder')}
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              className="w-full pl-10 pr-4 py-2.5 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-0"
              style={{}}
              onFocus={(e) => e.target.style.borderColor = aispaceColor}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>
        </div>
        <button
          className="flex items-center gap-2 px-5 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors"
          style={{ backgroundColor: aispaceColor }}
          onClick={onAdd}
        >
          <Plus size={18} />
          {t('manageOrganization.subAddButton')}
        </button>
      </div>

      {/* Empty State */}
      {isEmpty && (
        <div className="flex flex-col items-center justify-center py-20 px-8">
          <div className="w-16 h-16 bg-[#F3F4F6] rounded-full flex items-center justify-center mb-4">
            <Building2 size={28} className="text-[#9CA3AF]" />
          </div>
          <h3 className="text-[16px] font-medium text-[#111827] mb-1">{t('manageOrganization.subEmptyTitle')}</h3>
          <p className="text-[14px] text-[#6B7280]">{t('manageOrganization.subEmptyHint')}</p>
        </div>
      )}

      {/* No Search Results */}
      {noResults && (
        <div className="flex flex-col items-center justify-center py-20 px-8">
          <div className="w-16 h-16 bg-[#F3F4F6] rounded-full flex items-center justify-center mb-4">
            <Search size={28} className="text-[#9CA3AF]" />
          </div>
          <h3 className="text-[16px] font-medium text-[#111827] mb-1">{t('manageOrganization.noResultsTitle')}</h3>
          <p className="text-[14px] text-[#6B7280]">{t('manageOrganization.noResultsHint')}</p>
        </div>
      )}

      {/* Table */}
      {paginatedSubscriptions.length > 0 && (
        <>
          {/* Table Header */}
          <div className="flex items-center px-8 py-3 border-t border-b border-[#E5E7EB] bg-[#F9FAFB]">
            <div className="flex-1 text-[13px] font-medium text-[#6B7280]">{t('manageOrganization.subColumnName')}</div>
            <div className="flex-1 text-[13px] font-medium text-[#6B7280]">{t('manageOrganization.subColumnDescription')}</div>
            <div className="w-[50px] flex-shrink-0"></div>
          </div>

          {/* Table Body */}
          <div>
            {paginatedSubscriptions.map((sub, index) => {
              const isLastItem = paginatedSubscriptions.length > 3 && index === paginatedSubscriptions.length - 1;
              const isDropdownOpen = openDropdown === sub.subscription_id;

              return (
                <div
                  key={sub.subscription_id}
                  className={`flex items-center px-8 py-5 hover:bg-[#FAFAFA] transition-colors ${
                    index !== paginatedSubscriptions.length - 1 ? 'border-b border-[#E5E7EB]' : ''
                  }`}
                >
                  <div className="flex-1 text-[14px] font-medium text-[#111827] truncate pr-4">
                    {sub.subscription_name}
                  </div>
                  <div className="flex-1 text-[14px] text-[#6B7280] truncate pr-4">
                    {sub.subscription_description || '-'}
                  </div>
                  <div className="w-[50px] flex-shrink-0 flex items-center justify-end relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdown(isDropdownOpen ? null : sub.subscription_id);
                      }}
                      className="p-1.5 text-[#6B7280] hover:text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
                    >
                      <MoreHorizontal size={18} />
                    </button>

                    {isDropdownOpen && (
                      <div className={`absolute right-0 ${isLastItem ? 'bottom-full mb-2' : 'top-full mt-2'} w-[220px] bg-white border border-[#E5E7EB] rounded-xl shadow-lg z-[999] py-2`}>
                        <button
                          onClick={() => {
                            setOpenDropdown(null);
                            setViewUsageSub(sub);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors"
                        >
                          <Eye size={16} className="text-[#374151]" />
                          {t('manageOrganization.subActionViewUsage')}
                        </button>
                        <button
                          onClick={() => {
                            setOpenDropdown(null);
                            onEdit(sub);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors"
                        >
                          <Pencil size={16} className="text-[#374151]" />
                          {t('manageOrganization.subActionEdit')}
                        </button>
                        <button
                          onClick={() => {
                            setOpenDropdown(null);
                            onDelete(sub);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#EF4444] hover:bg-[#FEF2F2] transition-colors"
                        >
                          <Trash2 size={16} className="text-[#EF4444]" />
                          {t('manageOrganization.subActionDelete')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-8 py-4 border-t border-[#E5E7EB]">
            <span className="text-[14px] text-[#6B7280]">
              {t('pagination.pageOf', { current: currentPage, total: totalPages || 1 })}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.back')}
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ============== Credit Usage View ==============
const CreditUsageView = ({ subscription, aispaceColor, onBack }) => {
  const { t } = useTranslation();
  const taskLimits = subscription.task_limits || [];
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 7;
  const totalPages = Math.ceil(taskLimits.length / itemsPerPage);
  const paginatedLimits = taskLimits.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-[14px] text-[#6B7280] hover:text-[#374151] transition-colors mb-4"
      >
        <ArrowLeft size={16} />
        {t('manageOrganization.creditBackButton')}
      </button>

      <div className="bg-white border border-[#E5E7EB] rounded-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-4 border-b border-[#E5E7EB] bg-[#F9FAFB] rounded-t-xl">
          <div>
            <h3 className="text-[16px] font-semibold text-[#111827]">{t('manageOrganization.creditTitle')}</h3>
            <p className="text-[13px] text-[#6B7280] mt-0.5">{t('manageOrganization.creditSubtitle')}</p>
          </div>
        </div>

        {/* Table Header */}
        <div className="flex items-center px-8 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <div className="flex-1 text-[13px] font-medium text-[#6B7280]">{t('manageOrganization.creditColumnAgent')}</div>
          <div className="flex-1 text-[13px] font-medium text-[#6B7280]">{t('manageOrganization.creditColumnStandardPages')}</div>
          <div className="w-[170px] text-[13px] font-medium text-[#6B7280]">{t('manageOrganization.subModalUploadPagesColumn')}</div>
        </div>

        {/* Table Body */}
        <div>
          {paginatedLimits.length > 0 ? paginatedLimits.map((limit, index) => {
            const limitSet = limit.standard_pages_per_month != null && limit.standard_pages_per_month > 0;
            const used = limit.standard_pages_used_this_month || 0;
            const pagesPercent = limitSet ? Math.min(100, (used / limit.standard_pages_per_month) * 100) : 0;

            return (
              <div
                key={limit.task_id}
                className={`flex items-center px-8 py-5 hover:bg-[#FAFAFA] transition-colors ${
                  index !== paginatedLimits.length - 1 ? 'border-b border-[#E5E7EB]' : ''
                }`}
              >
                {/* Agent Name */}
                <div className="flex-1 text-[14px] font-medium text-[#111827] truncate pr-4">
                  {limit.task_name || t('manageOrganization.creditTaskFallback', { id: limit.task_id })}
                </div>

                {/* Standard-file pages: used / limit (or unlimited) */}
                <div className="flex-1 pr-4">
                  {limitSet ? (
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-[6px] bg-[#E5E7EB] rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${pagesPercent}%`, backgroundColor: aispaceColor }}
                        />
                      </div>
                      <span className="text-[13px] text-[#6B7280] whitespace-nowrap">
                        {used.toLocaleString()} / {limit.standard_pages_per_month.toLocaleString()}
                      </span>
                    </div>
                  ) : (
                    <span className="text-[13px] text-[#6B7280]">
                      {used.toLocaleString()} {t('manageOrganization.creditUnlimitedLabel')}
                    </span>
                  )}
                </div>

                {/* Upload pages per session (setting only, not a monthly usage number) */}
                <div className="w-[170px] text-[14px] text-[#374151]">
                  {limit.upload_pages_per_session != null
                    ? limit.upload_pages_per_session.toLocaleString()
                    : t('manageOrganization.creditUnlimitedLabel')}
                </div>
              </div>
            );
          }) : (
            <div className="flex flex-col items-center justify-center py-20 px-8">
              <div className="w-16 h-16 bg-[#F3F4F6] rounded-full flex items-center justify-center mb-4">
                <Building2 size={28} className="text-[#9CA3AF]" />
              </div>
              <h3 className="text-[16px] font-medium text-[#111827] mb-1">{t('manageOrganization.creditEmptyTitle')}</h3>
              <p className="text-[14px] text-[#6B7280]">{t('manageOrganization.creditEmptyHint')}</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {taskLimits.length > itemsPerPage && (
          <div className="flex items-center justify-between px-8 py-4 border-t border-[#E5E7EB]">
            <span className="text-[14px] text-[#6B7280]">
              {t('pagination.pageOf', { current: currentPage, total: totalPages || 1 })}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.back')}
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============== SharePoint Tab ==============
// ============== Knowledge Area Picker (custom dropdown + selected chip) ==============
// Matches the app's own custom-select pattern (see Overview.jsx's Department
// dropdown) instead of a native <select>, and shows the current pick as a
// removable chip (same pill style as AddCategoryModal's category chips)
// instead of leaving it as plain text inside the trigger button.
const KnowledgeAreaPicker = ({ knowledgeAreaId, setKnowledgeAreaId, knowledgeAreas, aispaceColor, t, disabled, onManage }) => {
  const [open, setOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedId = knowledgeAreaId || null;
  const selected = selectedId ? (knowledgeAreas || []).find(k => String(k.id) === selectedId) : null;

  // A site belongs to a single Knowledge Area — clicking the already-selected
  // one clears it, clicking any other one replaces the selection.
  const selectArea = (id) => {
    const idStr = String(id);
    setKnowledgeAreaId(selectedId === idStr ? null : idStr);
    setOpen(false);
  };

  const handleToggleOpen = () => {
    if (disabled) return;
    if (!open && ref.current) {
      // Estimate the panel's height (options + "create new" row) so it can
      // flip upward when there isn't enough room below it in the modal.
      const estimatedHeight = Math.min(240, (knowledgeAreas || []).length * 42 + 48) + 8;
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceBelow < estimatedHeight);
    }
    setOpen(!open);
  };

  return (
    <div>
      <label className="block text-[14px] font-semibold text-[#111827] mb-2">{t('manageOrganization.connectSiteKnowledgeAreaLabel')}</label>
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={handleToggleOpen}
          disabled={disabled}
          className="w-full px-4 py-3 pr-10 text-[14px] text-left border rounded-xl bg-white focus:outline-none cursor-pointer disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
          style={{ borderColor: open ? aispaceColor : '#E5E7EB' }}
        >
          {selected ? (
            <span className="text-[#111827]">{selected.name}</span>
          ) : (
            <span className="text-[#9CA3AF]">{t('manageOrganization.connectSiteKnowledgeAreaSelectPrompt')}</span>
          )}
        </button>
        <ChevronDown size={18} className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#6B7280] transition-transform ${open ? 'rotate-180' : ''}`} />
        {open && (
          <div className={`absolute z-50 w-full bg-white border border-[#E5E7EB] rounded-xl shadow-lg max-h-[240px] overflow-y-auto ${openUpward ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
            {onManage && (
              <button
                type="button"
                onClick={() => { setOpen(false); onManage(); }}
                className="sticky top-0 z-10 w-full text-left px-4 py-2.5 text-[14px] font-medium bg-white border-b border-[#E5E7EB] hover:bg-[#F9FAFB] transition-colors"
                style={{ color: aispaceColor }}
              >
                + {t('manageOrganization.knowledgeAreaCreateNew')}
              </button>
            )}
            {(knowledgeAreas || []).length === 0 && (
              <div className="px-4 py-2.5 text-[14px] text-[#9CA3AF]">{t('manageOrganization.connectSiteKnowledgeAreaEmpty')}</div>
            )}
            {(knowledgeAreas || []).map((k) => {
              const isSelected = selectedId === String(k.id);
              return (
                <div
                  key={k.id}
                  onClick={() => selectArea(k.id)}
                  className={`flex items-center justify-between px-4 py-2.5 text-[14px] cursor-pointer transition-colors ${isSelected ? 'font-medium' : 'text-[#374151] hover:bg-[#F9FAFB]'}`}
                  style={isSelected ? { backgroundColor: `${aispaceColor}1A`, color: aispaceColor } : {}}
                >
                  {k.name}
                  {isSelected && <Check size={16} />}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {selected && (
        <div className="flex flex-wrap gap-2 mt-3">
          <span className="inline-flex items-center gap-2 px-4 py-2 text-[14px] text-[#374151] bg-[#F3F4F6] border border-[#E5E7EB] rounded-full">
            {selected.name}
            <button type="button" onClick={() => setKnowledgeAreaId(null)} disabled={disabled} className="hover:text-[#111827] transition-colors disabled:opacity-50">
              <X size={16} />
            </button>
          </span>
        </div>
      )}
    </div>
  );
};

// ============== Connect Site Modal ==============
const ConnectSiteModal = ({ isOpen, onClose, organizationId, aispaceColor }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { siteCheckLoading, siteCheckResult, siteCheckError, addSiteLoading, addSiteError, knowledgeAreas } = useSelector(state => state.sharepoint);
  const [step, setStep] = useState(1);
  const [siteIdInput, setSiteIdInput] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [knowledgeAreaId, setKnowledgeAreaId] = useState(null);
  const [showKnowledgeAreaManager, setShowKnowledgeAreaManager] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  // Backend returns structured {code, site_name} for the duplicate-site case
  // (so it can be translated here) but still returns plain strings for other
  // errors (e.g. Graph/format failures) — handle both shapes.
  const formatSiteError = (err) => {
    if (!err) return '';
    if (typeof err === 'object') {
      if (err.code === 'duplicate_site') {
        return t('manageOrganization.connectSiteDuplicateSite', { name: err.site_name || '' });
      }
      if (err.code === 'invalid_site_id_format') {
        return t('manageOrganization.connectSiteInvalidIdFormat');
      }
      return formatSharePointError(err.message, t);
    }
    return formatSharePointError(err, t);
  };

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSiteIdInput('');
      setShowHelp(false);
      setDisplayName('');
      setKnowledgeAreaId(null);
      setConsentChecked(false);
      dispatch(clearSiteCheckResult());
      if (organizationId) dispatch(fetchKnowledgeAreas(organizationId));
    }
  }, [isOpen, organizationId, dispatch]);

  if (!isOpen) return null;

  const handleCheckAccess = async () => {
    const trimmed = siteIdInput.trim();
    if (!trimmed || siteCheckLoading) return;
    try {
      const result = await dispatch(checkSharePointSiteAccess({ orgId: organizationId, siteUrl: trimmed })).unwrap();
      setDisplayName(result.site_name || trimmed);
      setStep(2);
    } catch (err) {
      // Error surfaced below via siteCheckError
    }
  };

  const handleConnect = async () => {
    if (!consentChecked || addSiteLoading) return;
    try {
      await dispatch(addConfiguredSite({
        orgId: organizationId,
        siteUrl: siteIdInput.trim(),
        siteName: displayName.trim() || undefined,
        knowledgeAreaId: knowledgeAreaId || undefined,
      })).unwrap();
      dispatch(fetchConfiguredSites(organizationId));
      onClose();
    } catch (err) {
      // Error surfaced below via addSiteError
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && step === 1) {
      e.preventDefault();
      handleCheckAccess();
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]">
      <div className="bg-white rounded-xl w-[560px] shadow-xl flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="px-6 py-5 rounded-t-xl">
          <div className="flex items-center justify-between">
            <h2 className="text-[20px] font-bold text-[#111827]">{t('manageOrganization.connectSiteTitle')}</h2>
            <button onClick={onClose} className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors">
              <X size={20} className="text-[#6B7280]" />
            </button>
          </div>
          <p className="text-[13px] text-[#6B7280] mt-1">{t('manageOrganization.connectSiteSubtitle')}</p>
          {/* Step indicator */}
          <div className="flex items-center gap-3 mt-4">
            <div className="flex items-center gap-2">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-bold text-white flex-shrink-0`} style={{ backgroundColor: aispaceColor }}>1</span>
              <span className="text-[13px] font-medium" style={{ color: aispaceColor }}>{t('manageOrganization.connectSiteStep1')}</span>
            </div>
            <div className="flex-1 h-px bg-[#E5E7EB]" />
            <div className="flex items-center gap-2">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-bold flex-shrink-0 ${step === 2 ? 'text-white' : 'text-[#9CA3AF] bg-[#F3F4F6] border border-[#E5E7EB]'}`} style={step === 2 ? { backgroundColor: aispaceColor } : {}}>2</span>
              <span className={`text-[13px] font-medium ${step === 2 ? '' : 'text-[#9CA3AF]'}`} style={step === 2 ? { color: aispaceColor } : {}}>{t('manageOrganization.connectSiteStep2')}</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-5 space-y-4 flex-1 overflow-y-auto">
          {step === 1 && (
            <>
              <div>
                <label className="block text-[14px] font-semibold text-[#111827] mb-2">{t('manageOrganization.connectSiteIdLabel')}</label>
                <input
                  type="text"
                  className="w-full border border-[#E5E7EB] rounded-xl px-4 py-3.5 text-[14px] text-[#374151] focus:outline-none focus:ring-0 placeholder-[#9CA3AF]"
                  onFocus={(e) => e.target.style.borderColor = aispaceColor}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                  value={siteIdInput}
                  onChange={(e) => setSiteIdInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('manageOrganization.connectSiteUrlPlaceholder')}
                  disabled={siteCheckLoading}
                />
                <p className="text-[13px] text-[#6B7280] mt-2">{t('manageOrganization.connectSiteIdHint')}</p>
              </div>

              <div className="border border-[#E5E7EB] rounded-xl">
                <button
                  type="button"
                  onClick={() => setShowHelp(!showHelp)}
                  className="w-full flex items-center justify-between px-4 py-3 text-[14px] font-medium text-[#111827]"
                >
                  <span>{t('manageOrganization.connectSiteHelpTitle')}</span>
                  <ChevronDown size={18} className={`text-[#6B7280] transition-transform ${showHelp ? 'rotate-180' : ''}`} />
                </button>
                {showHelp && (
                  <div className="px-4 pb-4 text-[13px] text-[#6B7280] space-y-2">
                    <p>{t('manageOrganization.connectSiteHelpStep1')}</p>
                    <p>{t('manageOrganization.connectSiteHelpStep2')}</p>
                    <p>{t('manageOrganization.connectSiteHelpStep3')}</p>
                  </div>
                )}
              </div>

              {siteCheckError && (
                <p className="text-[13px] text-[#DC2626]">{formatSiteError(siteCheckError)}</p>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <div className="border border-[#E5E7EB] rounded-xl divide-y divide-[#E5E7EB]">
                <div className="px-4 py-3">
                  <p className="text-[12px] text-[#9CA3AF] mb-1">{t('manageOrganization.connectSiteUrlColumn')}</p>
                  <p className="text-[14px] text-[#111827] break-all">{siteCheckResult?.site_url || '—'}</p>
                </div>
                <div className="px-4 py-3">
                  <p className="text-[12px] text-[#9CA3AF] mb-1">{t('manageOrganization.connectSiteIdLabel')}</p>
                  <p className="text-[14px] text-[#111827] break-all">{siteIdInput.trim()}</p>
                </div>
                <div className="px-4 py-3 flex items-center justify-between">
                  <p className="text-[12px] text-[#9CA3AF]">{t('manageOrganization.connectSiteAccessColumn')}</p>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#ECFDF5] text-[#059669] text-[13px] font-medium">
                    <Check size={14} />
                    {t('manageOrganization.connectSiteAccessConfirmed')}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-[14px] font-semibold text-[#111827] mb-2">{t('manageOrganization.connectSiteDisplayNameLabel')}</label>
                <input
                  type="text"
                  maxLength={60}
                  className="w-full border border-[#E5E7EB] rounded-xl px-4 py-3 text-[14px] text-[#374151] focus:outline-none focus:ring-0"
                  onFocus={(e) => e.target.style.borderColor = aispaceColor}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
                <p className="text-[13px] text-[#6B7280] mt-2">{t('manageOrganization.connectSiteDisplayNameHint')}</p>
              </div>

              <div>
                <KnowledgeAreaPicker
                  knowledgeAreaId={knowledgeAreaId}
                  setKnowledgeAreaId={setKnowledgeAreaId}
                  knowledgeAreas={knowledgeAreas}
                  aispaceColor={aispaceColor}
                  t={t}
                  onManage={() => setShowKnowledgeAreaManager(true)}
                />
                <p className="text-[13px] text-[#6B7280] mt-2">{t('manageOrganization.connectSiteKnowledgeAreaHint')}</p>
              </div>

              <label className="flex items-start gap-3 border border-[#E5E7EB] rounded-xl px-4 py-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className="mt-0.5 w-4 h-4 flex-shrink-0"
                />
                <span className="text-[13px] text-[#374151]">{t('manageOrganization.connectSiteConsentLabel')}</span>
              </label>

              {addSiteError && (
                <p className="text-[13px] text-[#DC2626]">{formatSiteError(addSiteError)}</p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] rounded-b-xl">
          <button
            className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
            onClick={onClose}
          >
            {t('common.cancel')}
          </button>
          {step === 2 && (
            <button
              className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
              onClick={() => setStep(1)}
              disabled={addSiteLoading}
            >
              {t('common.back')}
            </button>
          )}
          {step === 1 ? (
            <button
              onClick={handleCheckAccess}
              disabled={!siteIdInput.trim() || siteCheckLoading}
              className="flex items-center justify-center gap-2 px-5 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
              style={{ backgroundColor: aispaceColor }}
            >
              {siteCheckLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t('manageOrganization.connectSiteCheckAccess')
              )}
            </button>
          ) : (
            <button
              onClick={handleConnect}
              disabled={!consentChecked || addSiteLoading}
              className="flex items-center justify-center gap-2 px-5 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
              style={{ backgroundColor: aispaceColor }}
            >
              {addSiteLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t('manageOrganization.connectSiteSubmit')
              )}
            </button>
          )}
        </div>
      </div>
    </div>
    {showKnowledgeAreaManager && (
      <KnowledgeAreaManagerModal
        orgId={organizationId}
        aispaceColor={aispaceColor}
        t={t}
        onClose={() => setShowKnowledgeAreaManager(false)}
      />
    )}
    </>
  );
};

// ============== Site Details Modal ==============
const formatDate = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
const formatDateTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return `${formatDate(iso)}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

const SiteDetailsModal = ({ site, onClose, onCheckAccessNow, checking, t }) => {
  if (!site) return null;
  const row = (label, value, subtext) => (
    <div className="px-4 py-3">
      <p className="text-[12px] text-[#9CA3AF] mb-1">{label}</p>
      {subtext && <p className="text-[11px] text-[#9CA3AF] mb-1">{subtext}</p>}
      <p className="text-[14px] text-[#111827] break-all">{value}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]" onClick={onClose}>
      <div className="bg-white rounded-xl w-[560px] shadow-xl overflow-hidden flex flex-col max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 flex items-start justify-between">
          <div>
            <h2 className="text-[20px] font-bold text-[#111827]">{site.site_name}</h2>
            <p className="text-[13px] text-[#6B7280] mt-1">{t('manageOrganization.spDetailsSubtitle')}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors flex-shrink-0">
            <X size={20} className="text-[#6B7280]" />
          </button>
        </div>

        <div className="px-6 pb-4 flex-1 overflow-y-auto">
          <div className="border border-[#E5E7EB] rounded-xl divide-y divide-[#E5E7EB]">
            {row(t('manageOrganization.connectSiteDisplayNameLabel'), site.site_name, t('manageOrganization.spDetailsSetInPlatform'))}
            {row(t('manageOrganization.spDetailsKnowledgeArea'), (site.knowledge_area_names && site.knowledge_area_names.length > 0) ? site.knowledge_area_names.join(', ') : '—')}
            {row(t('manageOrganization.spDetailsSharePointName'), site.graph_site_name || site.site_name)}
            {row(t('manageOrganization.connectSiteUrlColumn'), site.site_url)}
            <div className="px-4 py-3">
              <p className="text-[12px] text-[#9CA3AF] mb-1">{t('manageOrganization.connectSiteIdLabel')}</p>
              <p className="text-[11px] text-[#9CA3AF] mb-1">{t('manageOrganization.spDetailsIdentifiesSite')}</p>
              <p className="text-[14px] text-[#111827] break-all">
                {site.site_id}
                {site.site_id && <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] font-bold text-[#5B21B6] bg-[#EDE9FE] rounded align-middle">{t('manageOrganization.spDetailsKeyBadge')}</span>}
              </p>
            </div>
            <div className="px-4 py-3 flex items-center justify-between">
              <p className="text-[12px] text-[#9CA3AF]">{t('manageOrganization.connectSiteAccessColumn')}</p>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#ECFDF5] text-[#059669] text-[13px] font-medium">
                <Check size={14} />
                {t('manageOrganization.spDetailsAccessScope')}
              </span>
            </div>
            {row(t('manageOrganization.spDetailsConnectedBy'), site.connected_by_name || site.connected_by_email || '—')}
            {row(t('manageOrganization.spDetailsConnectedOn'), formatDate(site.created_at) || '—')}
            {row(
              t('manageOrganization.spDetailsAccessLastChecked'),
              site.access_last_checked_at
                ? t('manageOrganization.spDetailsCheckedByFormat', { datetime: formatDateTime(site.access_last_checked_at), name: site.access_last_checked_by_name || '—' })
                : t('manageOrganization.spNeverChecked')
            )}
            {row(
              t('manageOrganization.spColumnUsedByAgents'),
              (site.used_by_agents && site.used_by_agents.length > 0)
                ? site.used_by_agents.map(a => a.task_name).join(', ')
                : t('manageOrganization.spNotUsed')
            )}
          </div>
          <p className="text-[12px] text-[#9CA3AF] mt-4">{t('manageOrganization.spDetailsCheckAccessNote')}</p>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB]">
          <button
            className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
            onClick={onClose}
          >
            {t('common.close')}
          </button>
          <button
            onClick={() => onCheckAccessNow(site)}
            disabled={checking}
            className="flex items-center justify-center gap-2 px-5 py-2.5 text-[14px] font-medium text-[#111827] bg-white border-2 border-[#374151] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 min-w-[150px]"
          >
            {checking && <RefreshCw size={16} className="animate-spin" />}
            {t('manageOrganization.spActionCheckAccessNow')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============== Edit Name & Knowledge Area Modal ==============
const EditSiteModal = ({ site, organizationId, aispaceColor, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { knowledgeAreas } = useSelector(state => state.sharepoint);
  const [displayName, setDisplayName] = useState(site?.site_name || '');
  const [knowledgeAreaId, setKnowledgeAreaId] = useState((site?.knowledge_area_ids && site.knowledge_area_ids[0]) || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showKnowledgeAreaManager, setShowKnowledgeAreaManager] = useState(false);

  useEffect(() => {
    if (organizationId) dispatch(fetchKnowledgeAreas(organizationId));
  }, [organizationId, dispatch]);

  if (!site) return null;

  const handleSave = async () => {
    const trimmed = displayName.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    setError(null);
    try {
      await dispatch(updateConfiguredSite({
        orgId: organizationId,
        siteId: site.id,
        siteName: trimmed,
        knowledgeAreaId,
      })).unwrap();
      onSaved?.();
      onClose();
    } catch (err) {
      setError(typeof err === 'string' ? err : t('manageOrganization.spEditSiteSaveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]" onClick={() => { if (!saving) onClose() }}>
      <div className="bg-white rounded-xl w-[480px] shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 rounded-t-xl">
          <h2 className="text-[20px] font-bold text-[#111827]">{t('manageOrganization.spActionEditNameArea')}</h2>
          <button onClick={onClose} disabled={saving} className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors">
            <X size={20} className="text-[#6B7280]" />
          </button>
        </div>
        <div className="px-6 pb-5 space-y-4">
          <div>
            <label className="block text-[14px] font-semibold text-[#111827] mb-2">{t('manageOrganization.connectSiteDisplayNameLabel')}</label>
            <input
              type="text"
              maxLength={60}
              className="w-full border border-[#E5E7EB] rounded-xl px-4 py-3 text-[14px] text-[#374151] focus:outline-none focus:ring-0"
              onFocus={(e) => e.target.style.borderColor = aispaceColor}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={saving}
            />
          </div>
          <KnowledgeAreaPicker
            knowledgeAreaId={knowledgeAreaId}
            setKnowledgeAreaId={setKnowledgeAreaId}
            knowledgeAreas={knowledgeAreas}
            aispaceColor={aispaceColor}
            t={t}
            disabled={saving}
            onManage={() => setShowKnowledgeAreaManager(true)}
          />
          {error && <p className="text-[13px] text-[#DC2626]">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] rounded-b-xl">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={!displayName.trim() || saving}
            className="flex items-center justify-center gap-2 px-5 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 min-w-[120px]"
            style={{ backgroundColor: aispaceColor }}
          >
            {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : t('common.save')}
          </button>
        </div>
      </div>
    </div>
    {showKnowledgeAreaManager && (
      <KnowledgeAreaManagerModal
        orgId={organizationId}
        aispaceColor={aispaceColor}
        t={t}
        onClose={() => setShowKnowledgeAreaManager(false)}
      />
    )}
    </>
  );
};

// ============== Site Agents Modal (view / disconnect-blocked) ==============
const SiteAgentsModal = ({ mode, siteName, agents, aispaceColor, onClose, navigate, t }) => {
  if (!agents) return null;

  const handleOpenAgent = (taskId) => {
    onClose();
    navigate(`/agents/settings/${taskId}`);
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[140]" onClick={onClose}>
      <div className="bg-white rounded-xl w-[520px] shadow-xl overflow-hidden flex flex-col max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5">
          <h2 className="text-[20px] font-bold text-[#111827]">
            {mode === 'blocked' ? t('manageOrganization.spAgentsModalBlockedTitle') : t('manageOrganization.spAgentsModalViewTitle')}
          </h2>
          <p className="text-[13px] text-[#6B7280] mt-1">
            {mode === 'blocked' ? t('manageOrganization.spAgentsModalBlockedSubtitle') : t('manageOrganization.spAgentsModalViewSubtitle')}
          </p>
        </div>

        <div className="px-6 pb-4 flex-1 overflow-y-auto">
          {mode === 'blocked' && (
            <div className="flex items-start gap-3 px-4 py-3 bg-[#FEF3C7] border border-[#FDE68A] rounded-lg mb-4">
              <AlertTriangle size={18} className="text-[#D97706] flex-shrink-0 mt-0.5" />
              <p className="text-[13px] text-[#92400E]">
                {agents.length === 1
                  ? t('manageOrganization.spAgentsModalWarningSingular', { siteName, count: 1 })
                  : t('manageOrganization.spAgentsModalWarningPlural', { siteName, count: agents.length })}
              </p>
            </div>
          )}

          <div className="border border-[#E5E7EB] rounded-xl divide-y divide-[#E5E7EB]">
            {agents.map((a) => (
              <div key={a.task_id} className="px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold text-[#111827] truncate">{a.task_name}</p>
                  <p className="text-[12px] text-[#9CA3AF]">
                    {(a.file_count || 0) === 1
                      ? t('manageOrganization.spAgentsModalFileCountSingular', { count: 1 })
                      : t('manageOrganization.spAgentsModalFileCountPlural', { count: a.file_count || 0 })}
                    {a.imported_at ? ` · ${t('manageOrganization.spAgentsModalImportedOn', { date: formatDate(a.imported_at) })}` : ''}
                  </p>
                </div>
                <button
                  onClick={() => handleOpenAgent(a.task_id)}
                  className="px-4 py-2 text-[13px] font-semibold text-[#111827] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors flex-shrink-0"
                >
                  {t('manageOrganization.spAgentsModalOpenAgent')}
                </button>
              </div>
            ))}
          </div>

          {mode === 'blocked' && (
            <p className="text-[12px] text-[#9CA3AF] mt-4">{t('manageOrganization.spAgentsModalDeleteNote')}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB]">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
          >
            {t('common.close')}
          </button>
          {mode === 'blocked' && agents.length > 0 && (
            <button
              onClick={() => handleOpenAgent(agents[0].task_id)}
              className="px-5 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors"
              style={{ backgroundColor: aispaceColor }}
            >
              {t('manageOrganization.spAgentsModalGoToFirst')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const SharePointTab = ({ organizationId, organizationName, aispaceColor, onConnect, addToast }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { connection, connectionLoading, configuredSites, configuredSitesLoading, existingSiteCheckLoadingId } = useSelector(state => state.sharepoint);
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [departmentFilterOpen, setDepartmentFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [connectSiteModalOpen, setConnectSiteModalOpen] = useState(false);
  const [connectionFetched, setConnectionFetched] = useState(false);
  const [sitesFetched, setSitesFetched] = useState(false);
  const [siteDropdown, setSiteDropdown] = useState(null); // site id for open dropdown
  const [siteDropdownUpward, setSiteDropdownUpward] = useState(false);
  const [agentsViewModal, setAgentsViewModal] = useState(null); // site object for "N agents" view modal
  const [agentsHoverPreview, setAgentsHoverPreview] = useState(null); // site id for hover chips preview
  const [disconnectBlockedModal, setDisconnectBlockedModal] = useState(null); // {siteName, agents} when disconnect is blocked
  const [syncIntervalModal, setSyncIntervalModal] = useState(null); // site object for modal
  const [syncIntervalValue, setSyncIntervalValue] = useState('');
  const [syncIntervalSaving, setSyncIntervalSaving] = useState(false);
  const [deleteSiteModal, setDeleteSiteModal] = useState(null); // site object for disconnect confirm
  const [deletingSite, setDeletingSite] = useState(false);
  const [siteDetailsModal, setSiteDetailsModal] = useState(null); // site object for "View site details"
  const [editSiteModal, setEditSiteModal] = useState(null); // site object for "Edit name and knowledge area"

  const handleCheckAccessNow = async (site) => {
    try {
      const result = await dispatch(checkExistingSiteAccess({ orgId: organizationId, siteId: site.id })).unwrap();
      addToast?.(t('manageOrganization.spToastAccessConfirmed'), 'success');
      return result;
    } catch (err) {
      addToast?.(t('manageOrganization.spToastAccessFailed', { error: err?.detail || '' }), 'error');
      return null;
    }
  };

  const handleCopySiteId = (site) => {
    if (!site.site_id) return;
    navigator.clipboard.writeText(site.site_id).then(() => {
      addToast?.(t('manageOrganization.spToastSiteIdCopied'), 'success');
    }).catch(() => {});
  };

  const handleDisconnectClick = (site) => {
    if (site.used_by_agents && site.used_by_agents.length > 0) {
      setDisconnectBlockedModal({ siteName: site.site_name, agents: site.used_by_agents });
      return;
    }
    setDeleteSiteModal(site);
  };
  const itemsPerPage = 7;

  useEffect(() => {
    if (organizationId) {
      setConnectionFetched(false);
      setSitesFetched(false);
      dispatch(fetchSharePointConnection(organizationId)).finally(() => {
        setConnectionFetched(true);
      });
      dispatch(fetchConfiguredSites(organizationId)).finally(() => {
        setSitesFetched(true);
      });
      dispatch(fetchKnowledgeAreas(organizationId));
    }
  }, [dispatch, organizationId]);

  const showConnectionButtons = connectionFetched && !connectionLoading;
  const isSitesLoading = !sitesFetched || configuredSitesLoading;

  const knowledgeAreaOptions = Array.from(
    new Map((configuredSites || [])
      .flatMap(s => (s.knowledge_area_ids || []).map((id, i) => [id, (s.knowledge_area_names || [])[i]]))
      .filter(([id, name]) => id && name)
    ).entries()
  );

  const filteredSites = (configuredSites || []).filter(site => {
    const matchesSearch = site.site_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      site.site_url?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDepartment = !departmentFilter || (site.knowledge_area_ids || []).includes(departmentFilter);
    return matchesSearch && matchesDepartment;
  });

  const totalPages = Math.ceil(filteredSites.length / itemsPerPage);
  const paginatedSites = filteredSites.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const isEmpty = filteredSites.length === 0 && !searchQuery;
  const noResults = filteredSites.length === 0 && searchQuery;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl">
      {/* Search and Action Buttons */}
      <div className="flex items-center justify-between gap-4 px-8 py-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input
              type="text"
              placeholder={t('manageOrganization.spSearchPlaceholder')}
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              className="w-full pl-10 pr-4 py-2.5 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-0"
              onFocus={(e) => e.target.style.borderColor = aispaceColor}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>
          <div className="relative">
            <button
              onClick={() => setDepartmentFilterOpen(!departmentFilterOpen)}
              className="flex items-center gap-2 px-4 py-2.5 text-[14px] text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
            >
              {knowledgeAreaOptions.find(([id]) => id === departmentFilter)?.[1] || t('manageOrganization.spAllKnowledgeAreas')}
              <ChevronDown size={16} className="text-[#6B7280]" />
            </button>
            {departmentFilterOpen && (
              <div className="absolute left-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-xl shadow-lg z-20 min-w-[200px] p-1.5">
                <button
                  onClick={() => { setDepartmentFilter(''); setDepartmentFilterOpen(false); setCurrentPage(1); }}
                  className="w-full px-3 py-2 rounded-lg text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors"
                >
                  {t('manageOrganization.spAllKnowledgeAreas')}
                </button>
                {knowledgeAreaOptions.map(([id, name]) => (
                  <button
                    key={id}
                    onClick={() => { setDepartmentFilter(id); setDepartmentFilterOpen(false); setCurrentPage(1); }}
                    className="w-full px-3 py-2 rounded-lg text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors"
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {showConnectionButtons && (
            connection ? (
              <>
                {/* Edit SharePoint - only when connection exists */}
                <button
                  onClick={onConnect}
                  className="flex items-center gap-2 px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-[#F3F4F6] border border-[#E5E7EB] rounded-lg hover:bg-[#E5E7EB] transition-colors"
                >
                  <Pencil size={16} />
                  {t('manageOrganization.spEditButton')}
                </button>
                {/* Connect Site - only when SharePoint connection exists */}
                <button
                  className="flex items-center gap-2 px-5 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors"
                  style={{ backgroundColor: aispaceColor }}
                  onClick={() => setConnectSiteModalOpen(true)}
                >
                  <Plus size={18} />
                  {t('manageOrganization.spConnectSiteButton')}
                </button>
              </>
            ) : (
              /* No connection — only show Connect SharePoint button */
              <button
                className="flex items-center gap-2 px-5 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors"
                style={{ backgroundColor: aispaceColor }}
                onClick={onConnect}
              >
                <Plus size={18} />
                {t('manageOrganization.spConnectButton')}
              </button>
            )
          )}
        </div>
      </div>

      {/* Info banner */}
      {!isEmpty && (
        <div className="mx-8 mb-4 px-4 py-3 bg-[#F5F3FF] border border-[#E9E5FF] rounded-lg text-[13px] text-[#5B21B6]">
          <span className="font-semibold">{t('manageOrganization.spInfoBannerTitle')}</span>{' '}
          {t('manageOrganization.spInfoBannerBody')}
        </div>
      )}

      {/* Empty State */}
      {isEmpty && !isSitesLoading && (
        <div className="flex flex-col items-center justify-center py-20 px-8">
          {/* Decorative circle with + icon */}
          <div className="relative mb-6">
            <div className="w-[120px] h-[120px] rounded-full border-2 border-dashed border-[#EEEEEE] flex items-center justify-center relative">
              {/* Decorative dots */}
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#EEEEEE]"></div>
              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#EEEEEE]"></div>
              <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 rounded-full bg-[#EEEEEE]"></div>
              <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-2 h-2 rounded-full bg-[#EEEEEE]"></div>
              {/* Inner circle with + icon */}
              <div className="w-16 h-16 rounded-full bg-[#F9FAFB] border border-[#EEEEEE] flex items-center justify-center">
                <Plus size={28} className="text-[#9CA3AF]" />
              </div>
            </div>
          </div>
          <h3 className="text-[16px] font-semibold text-[#111827] mb-2">{t('manageOrganization.spEmptyTitle')}</h3>
          <p className="text-[14px] text-[#6B7280] text-center max-w-md">
            {t('manageOrganization.spEmptyHint')}
          </p>
        </div>
      )}

      {/* Loading */}
      {isSitesLoading && (
        <div className="flex justify-center items-center py-20">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: aispaceColor, borderTopColor: 'transparent' }}></div>
        </div>
      )}

      {/* No Search Results */}
      {noResults && !isSitesLoading && (
        <div className="flex flex-col items-center justify-center py-20 px-8">
          <div className="w-16 h-16 bg-[#F3F4F6] rounded-full flex items-center justify-center mb-4">
            <Search size={28} className="text-[#9CA3AF]" />
          </div>
          <h3 className="text-[16px] font-medium text-[#111827] mb-1">{t('manageOrganization.noResultsTitle')}</h3>
          <p className="text-[14px] text-[#6B7280]">{t('manageOrganization.noResultsHint')}</p>
        </div>
      )}

      {/* Table */}
      {paginatedSites.length > 0 && !isSitesLoading && (
        <>
          {/* Table Header */}
          <div className="flex items-center px-8 py-3 border-t border-b border-[#E5E7EB] bg-[#F9FAFB]">
            <div className="flex-[1.5] text-[13px] font-medium text-[#6B7280]">{t('manageOrganization.spColumnSite')}</div>
            <div className="flex-1 text-[13px] font-medium text-[#6B7280]">{t('manageOrganization.spColumnKnowledgeArea')}</div>
            <div className="flex-1 text-[13px] font-medium text-[#6B7280]">{t('manageOrganization.spColumnStatus')}</div>
            <div className="flex-1 text-[13px] font-medium text-[#6B7280]">{t('manageOrganization.spColumnUsedByAgents')}</div>
            <div className="w-[60px]"></div>
          </div>

          {/* Table Body */}
          <div>
            {paginatedSites.map((site, index) => (
              <div
                key={site.id}
                className={`flex items-center px-8 py-5 hover:bg-[#FAFAFA] transition-colors ${
                  index !== paginatedSites.length - 1 ? 'border-b border-[#E5E7EB]' : ''
                }`}
              >
                <div className="flex-[1.5] flex items-center gap-2 truncate pr-4">
                  <Globe size={16} className="text-[#6B7280] flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[14px] font-medium text-[#111827] truncate">{site.site_name}</p>
                    <p className="text-[12px] text-[#9CA3AF] truncate">{site.site_url}</p>
                  </div>
                </div>
                <div className="flex-1 pr-4">
                  {(site.knowledge_area_names && site.knowledge_area_names.length > 0) ? (
                    <div className="flex flex-wrap gap-1.5">
                      {site.knowledge_area_names.map((name, i) => (
                        <span key={i} className="inline-block px-2.5 py-1 text-[12px] font-medium text-[#374151] bg-[#F3F4F6] rounded-md">
                          {name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-[14px] text-[#9CA3AF]">—</span>
                  )}
                </div>
                <div className="flex-1">
                  {/* Connecting a site only grants Graph access (validated at
                      connect time, before the row ever exists here) — there is
                      no separate "sync" step in this access-only model, so
                      every listed site is "Connected" unless it has a real
                      last_error (e.g. the admin's Graph access was later
                      revoked). The old sync_status ('synced'/'pending') was
                      for the unrelated auto-sync/full-import flow and never
                      gets updated by this connect flow — showing it here just
                      left every site stuck on its DB-default 'Pending'. */}
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${site.last_error ? 'bg-red-500' : 'bg-green-500'}`} />
                    <span className="text-[14px] font-medium text-[#111827]">
                      {site.last_error ? t('manageOrganization.spStatusError') : t('manageOrganization.spStatusConnected')}
                    </span>
                  </div>
                  <p className="text-[12px] text-[#9CA3AF] mt-0.5">
                    {site.last_error ? formatSharePointError(site.last_error, t) : t('manageOrganization.spStatusReadOnlyAccess')}
                  </p>
                </div>
                <div
                  className="flex-1 relative"
                  onMouseEnter={() => { if (site.used_by_agents?.length > 0) setAgentsHoverPreview(site.id) }}
                  onMouseLeave={() => setAgentsHoverPreview((current) => (current === site.id ? null : current))}
                >
                  {(site.used_by_agents && site.used_by_agents.length > 0) ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSiteDropdown(null); setAgentsViewModal(site) }}
                      className="text-[14px] font-medium hover:underline"
                      style={{ color: aispaceColor }}
                    >
                      {site.used_by_agents.length === 1
                        ? t('manageOrganization.spUsedByAgentsSingular', { count: 1 })
                        : t('manageOrganization.spUsedByAgentsPlural', { count: site.used_by_agents.length })}
                    </button>
                  ) : (
                    <>
                      <p className="text-[14px] text-[#6B7280]">{t('manageOrganization.spNotUsed')}</p>
                      <p className="text-[12px] text-[#9CA3AF]">{t('manageOrganization.spCanBeDisconnected')}</p>
                    </>
                  )}
                  {/* Hover preview — quick glance at all agent names, tinted with
                      the org's own theme color (aispaceColor) so it's dynamic
                      per organization, not a fixed gray. Clicking the count
                      above still opens the full modal with "Open agent" actions. */}
                  {agentsHoverPreview === site.id && (
                    <div className="absolute left-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-xl shadow-lg z-20 min-w-[240px] max-w-[320px] p-3">
                      <div className="flex flex-wrap gap-1.5">
                        {site.used_by_agents.map((a) => (
                          <span
                            key={a.task_id}
                            className="px-2.5 py-1 text-[12px] font-medium rounded-full"
                            style={{ color: aispaceColor, backgroundColor: `${aispaceColor}1A` }}
                          >
                            {a.task_name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {/* 3-dot menu */}
                <div className="w-[60px] relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (siteDropdown === site.id) {
                        setSiteDropdown(null);
                      } else {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const menuHeight = 240; // approx height of the 5-item menu
                        setSiteDropdownUpward(window.innerHeight - rect.bottom < menuHeight);
                        setSiteDropdown(site.id);
                      }
                    }}
                    className="p-1.5 border border-[#E5E7EB] hover:bg-[#F3F4F6] rounded-lg transition-colors"
                  >
                    <MoreHorizontal size={18} className="text-[#6B7280]" />
                  </button>
                  {siteDropdown === site.id && (
                    <div className={`absolute right-0 bg-white border border-[#E5E7EB] rounded-xl shadow-lg z-20 min-w-[240px] p-1.5 ${siteDropdownUpward ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
                      <button
                        onClick={() => { setSiteDropdown(null); setSiteDetailsModal(site) }}
                        className="w-full px-3 py-2.5 flex items-center gap-3 rounded-lg text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors"
                      >
                        <Eye size={16} className="text-[#374151]" />
                        <span>{t('manageOrganization.spActionViewDetails')}</span>
                      </button>
                      <button
                        onClick={() => { setSiteDropdown(null); setEditSiteModal(site) }}
                        className="w-full px-3 py-2.5 flex items-center gap-3 rounded-lg text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors"
                      >
                        <Pencil size={16} className="text-[#374151]" />
                        <span>{t('manageOrganization.spActionEditNameArea')}</span>
                      </button>
                      <button
                        onClick={() => { setSiteDropdown(null); handleCheckAccessNow(site) }}
                        disabled={existingSiteCheckLoadingId === site.id}
                        className="w-full px-3 py-2.5 flex items-center gap-3 rounded-lg text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors disabled:opacity-50"
                      >
                        <RefreshCw size={16} className={`text-[#374151] ${existingSiteCheckLoadingId === site.id ? 'animate-spin' : ''}`} />
                        <span>{t('manageOrganization.spActionCheckAccessNow')}</span>
                      </button>
                      <button
                        onClick={() => { setSiteDropdown(null); handleCopySiteId(site) }}
                        className="w-full px-3 py-2.5 flex items-center gap-3 rounded-lg text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors"
                      >
                        <Copy size={16} className="text-[#374151]" />
                        <span>{t('manageOrganization.spActionCopySiteId')}</span>
                      </button>
                      <div className="my-1 border-t border-[#E5E7EB]" />
                      <button
                        onClick={() => { setSiteDropdown(null); handleDisconnectClick(site) }}
                        className="w-full px-3 py-2.5 flex items-center gap-3 rounded-lg text-left text-[14px] text-[#EF4444] hover:bg-[#FEF2F2] transition-colors"
                      >
                        <WifiOff size={16} className="text-[#EF4444]" />
                        <span>{t('manageOrganization.spActionDisconnect')}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Sync Interval Modal */}
          {syncIntervalModal && (
            <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]" onClick={() => setSyncIntervalModal(null)}>
              <div className="bg-white rounded-2xl w-[520px] shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB] rounded-t-2xl">
                  <h2 className="text-[20px] font-bold text-[#111827]">{t('manageOrganization.syncIntervalTitle')}</h2>
                  <button onClick={() => setSyncIntervalModal(null)} className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors">
                    <X size={20} className="text-[#6B7280]" />
                  </button>
                </div>
                <div className="px-6 py-5">
                  <label className="block text-[14px] font-semibold text-[#111827] mb-2">{t('manageOrganization.syncIntervalLabel')}</label>
                  <input
                    type="number"
                    min="1"
                    value={syncIntervalValue}
                    onChange={(e) => setSyncIntervalValue(e.target.value)}
                    className="w-full border border-[#E5E7EB] rounded-xl px-4 py-3 text-[14px] text-[#374151] focus:outline-none focus:ring-0"
                    onFocus={(e) => e.target.style.borderColor = aispaceColor}
                    onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                    disabled={syncIntervalSaving}
                  />
                  <p className="text-[12px] text-[#9CA3AF] mt-2">
                    {t('manageOrganization.syncIntervalHint')}
                  </p>
                </div>
                <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] rounded-b-2xl">
                  <button
                    onClick={() => setSyncIntervalModal(null)}
                    disabled={syncIntervalSaving}
                    className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={async () => {
                      const val = parseInt(syncIntervalValue) || 15
                      setSyncIntervalSaving(true)
                      try {
                        await dispatch(updateConfiguredSite({ orgId: organizationId, siteId: syncIntervalModal.id, syncIntervalMinutes: val })).unwrap()
                        dispatch(fetchConfiguredSites(organizationId))
                        setSyncIntervalModal(null)
                      } catch (err) {
                        console.error('Failed to update interval:', err)
                      } finally {
                        setSyncIntervalSaving(false)
                      }
                    }}
                    disabled={syncIntervalSaving}
                    className="flex items-center justify-center gap-2 px-6 py-2.5 text-[14px] font-semibold text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 min-w-[120px]"
                    style={{ backgroundColor: aispaceColor }}
                  >
                    {syncIntervalSaving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>{t('manageOrganization.syncIntervalSaving')}</span>
                      </>
                    ) : (
                      <span>{t('manageOrganization.syncIntervalSubmit')}</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Disconnect Site Confirmation Modal */}
          {deleteSiteModal && (
            <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[130]" onClick={() => { if (!deletingSite) setDeleteSiteModal(null) }}>
              <div className="bg-white rounded-2xl w-[420px] shadow-xl px-6 pt-8 pb-6 mx-4" onClick={(e) => e.stopPropagation()}>
                {/* Warning Icon */}
                <div className="flex justify-center mb-4">
                  <div className="w-14 h-14 rounded-full bg-[#FEE2E2] flex items-center justify-center">
                    <WifiOff size={24} className="text-[#DC2626]" />
                  </div>
                </div>
                <h2 className="text-[18px] font-bold text-[#111827] text-center mb-2">{t('manageOrganization.deleteSiteTitle')}</h2>
                <p className="text-[14px] text-[#6B7280] text-center mb-6">
                  {t('manageOrganization.deleteSiteMessage', { name: deleteSiteModal.site_name })}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setDeleteSiteModal(null)}
                    disabled={deletingSite}
                    className="flex-1 px-4 py-3 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={async () => {
                      setDeletingSite(true)
                      try {
                        await dispatch(removeConfiguredSite({ orgId: organizationId, siteId: deleteSiteModal.id })).unwrap()
                        dispatch(fetchConfiguredSites(organizationId))
                        setDeleteSiteModal(null)
                      } catch (err) {
                        // Defense-in-depth: the client already blocks this click when
                        // used_by_agents is non-empty (handleDisconnectClick), but the
                        // list could be stale — the backend re-checks and returns this
                        // same structured error if an agent started using it meanwhile.
                        if (err && typeof err === 'object' && err.code === 'site_in_use') {
                          setDeleteSiteModal(null)
                          setDisconnectBlockedModal({ siteName: err.site_name || deleteSiteModal.site_name, agents: err.agents || [] })
                        } else {
                          console.error('Failed to disconnect site:', err)
                        }
                      } finally {
                        setDeletingSite(false)
                      }
                    }}
                    disabled={deletingSite}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-[14px] font-semibold text-white bg-[#DC2626] rounded-lg hover:bg-[#B91C1C] transition-colors disabled:opacity-50"
                  >
                    {deletingSite ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>{t('manageOrganization.deleteSiteDeleting')}</span>
                      </>
                    ) : (
                      <span>{t('manageOrganization.spActionDisconnect')}</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between px-8 py-4 border-t border-[#E5E7EB]">
            <span className="text-[14px] text-[#6B7280]">
              {t('pagination.pageOf', { current: currentPage, total: totalPages || 1 })}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.back')}
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Connect Site Modal */}
      <ConnectSiteModal
        isOpen={connectSiteModalOpen}
        onClose={() => setConnectSiteModalOpen(false)}
        organizationId={organizationId}
        aispaceColor={aispaceColor}
      />

      {/* View Site Details Modal — looked up live from configuredSites so
          "Check access now" (clicked from inside this modal) refreshes what's
          shown here instead of the stale snapshot from when it was opened. */}
      {siteDetailsModal && (
        <SiteDetailsModal
          site={configuredSites.find(s => s.id === siteDetailsModal.id) || siteDetailsModal}
          onClose={() => setSiteDetailsModal(null)}
          onCheckAccessNow={handleCheckAccessNow}
          checking={existingSiteCheckLoadingId === siteDetailsModal.id}
          t={t}
        />
      )}

      {/* Edit Name & Knowledge Area Modal */}
      {editSiteModal && (
        <EditSiteModal
          site={editSiteModal}
          organizationId={organizationId}
          aispaceColor={aispaceColor}
          onClose={() => setEditSiteModal(null)}
          onSaved={() => dispatch(fetchConfiguredSites(organizationId))}
        />
      )}

      {/* "N agents" click — view/open agents using this site */}
      {agentsViewModal && (
        <SiteAgentsModal
          mode="view"
          siteName={agentsViewModal.site_name}
          agents={agentsViewModal.used_by_agents}
          aispaceColor={aispaceColor}
          onClose={() => setAgentsViewModal(null)}
          navigate={navigate}
          t={t}
        />
      )}

      {/* Disconnect blocked — must remove site from these agents first */}
      {disconnectBlockedModal && (
        <SiteAgentsModal
          mode="blocked"
          siteName={disconnectBlockedModal.siteName}
          agents={disconnectBlockedModal.agents}
          aispaceColor={aispaceColor}
          onClose={() => setDisconnectBlockedModal(null)}
          navigate={navigate}
          t={t}
        />
      )}
    </div>
  );
};

export default ManageOrganization;
